import React, { useState, useEffect } from 'react';
import { Star, User, Camera, X, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getImageUrl } from '../lib/utils';

const ProductReviews = ({ productId }) => {
  const { isAuthenticated, user, login } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    rating: 5,
    comment: '',
    images: [],
    imageUrls: [] // Uploaded image URLs from server
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/reviews/${productId}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        setAverageRating(data.average_rating || 0);
        setTotalReviews(data.total_reviews || 0);
      }
    } catch (error) {
      console.error('Reviews fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Limit to 4 images
    const remainingSlots = 4 - formData.imageUrls.length;
    const filesToUpload = files.slice(0, remainingSlots);
    
    setIsUploading(true);
    setError('');
    
    for (const file of filesToUpload) {
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, reader.result].slice(0, 4)
        }));
      };
      reader.readAsDataURL(file);
      
      // Upload to server
      try {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const response = await fetch(`${backendUrl}/api/reviews/upload-image`, {
          method: 'POST',
          credentials: 'include',
          body: uploadFormData
        });
        
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            imageUrls: [...prev.imageUrls, data.image_url].slice(0, 4)
          }));
        } else {
          console.error('Image upload failed');
          setError('Görsel yüklenemedi');
        }
      } catch (err) {
        console.error('Image upload error:', err);
        setError('Görsel yüklenirken bir hata oluştu');
      }
    }
    
    setIsUploading(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imageUrls: prev.imageUrls.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!isAuthenticated) {
      login();
      return;
    }

    if (!formData.comment.trim()) {
      setError('Lütfen bir yorum yazın');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${backendUrl}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: productId,
          rating: formData.rating,
          comment: formData.comment,
          images: formData.imageUrls // Send uploaded image URLs
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Yorum gönderilemedi');
      }

      setSuccess('Yorumunuz başarıyla eklendi!');
      setFormData({ rating: 5, comment: '', images: [], imageUrls: [] });
      setShowForm(false);
      fetchReviews(); // Refresh reviews
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const renderStars = (rating, interactive = false, size = 16) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => handleRatingClick(star) : undefined}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
            disabled={!interactive}
          >
            <Star
              size={size}
              fill={star <= rating ? '#fbbf24' : 'none'}
              className={star <= rating ? 'text-yellow-400' : 'text-gray-300'}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="border-t pt-12 mt-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-light tracking-wider mb-2">DEĞERLENDIRMELER</h2>
          <div className="flex items-center gap-3">
            {renderStars(Math.round(averageRating))}
            <span className="text-lg font-medium">{averageRating.toFixed(1)}</span>
            <span className="text-gray-500">({totalReviews} yorum)</span>
          </div>
        </div>
        
        {isAuthenticated ? (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-black text-white px-6 py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors"
          >
            {showForm ? 'İPTAL' : 'YORUM YAZ'}
          </button>
        ) : (
          <button
            onClick={login}
            className="bg-black text-white px-6 py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors"
          >
            YORUM YAPMAK İÇİN GİRİŞ YAP
          </button>
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded">
          {error}
        </div>
      )}

      {/* Review Form */}
      {showForm && isAuthenticated && (
        <form onSubmit={handleSubmit} className="bg-gray-50 p-6 mb-8">
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Puanınız</label>
            {renderStars(formData.rating, true, 28)}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Yorumunuz</label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
              rows={4}
              placeholder="Bu ürün hakkındaki düşüncelerinizi paylaşın..."
              className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black resize-none"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Fotoğraf Ekle (Opsiyonel)</label>
            <div className="flex flex-wrap gap-3">
              {formData.images.map((img, index) => (
                <div key={index} className="relative">
                  <img src={getImageUrl(img)} alt="" className="w-20 h-20 object-cover rounded" />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {formData.images.length < 4 && !isUploading && (
                <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-black transition-colors">
                  <Camera size={20} className="text-gray-400" />
                  <span className="text-xs text-gray-400 mt-1">Ekle</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}
              {isUploading && (
                <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-gray-400 mt-1">Yükleniyor</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">En fazla 4 fotoğraf ekleyebilirsiniz (JPEG, PNG, WebP - max 5MB)</p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isUploading}
            className="flex items-center gap-2 bg-black text-white px-6 py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                GÖNDERİLİYOR...
              </>
            ) : isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                GÖRSEL YÜKLENİYOR...
              </>
            ) : (
              <>
                <Send size={16} />
                YORUMU GÖNDER
              </>
            )}
          </button>
        </form>
      )}

      {/* Reviews List */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.review_id} className="border-b pb-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {review.user_picture ? (
                    <img
                      src={review.user_picture}
                      alt={review.user_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <User size={18} className="text-gray-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{review.user_name}</p>
                    <p className="text-xs text-gray-500">{formatDate(review.created_at)}</p>
                  </div>
                </div>
                {renderStars(review.rating)}
              </div>
              
              <p className="text-gray-700 mb-3">{review.comment}</p>
              
              {review.images && review.images.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {review.images.map((img, index) => {
                    // Handle both relative API paths and full URLs
                    const imgSrc = img.startsWith('/api/') ? `${backendUrl}${img}` : img;
                    return (
                      <img
                        key={index}
                        src={imgSrc}
                        alt=""
                        className="w-24 h-24 object-cover rounded cursor-pointer hover:opacity-90"
                        onClick={() => window.open(imgSrc, '_blank')}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50">
          <Star size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">Henüz yorum yapılmamış</p>
          <p className="text-sm text-gray-400">Bu ürün hakkında ilk yorumu siz yapın!</p>
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
